import { computed, inject, ref, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { merge } from "lodash-es";
import ActivitiesApi from "@/api/activities";
import AiApi from "@/api/ai";
import TasksApi from "@/api/tasks";
import DiscussionsApi from "@/api/discussions";
import { useContext } from "@/store/context";
import { useToastNotificationStore } from "@/experiences/toast-notifications/store/toast-notifications";
import { useCrossTabCom } from "@/common/composables/cross-tab-communication";
import { useStudyUsers } from "@/store/study-users.store";
import { useStudiesStore } from "@/store/studies";
import { useSegmentsStore } from "@/store/segments";
import { useQnA } from "@/experiences/qna/composables";
import { useAiIngestStore } from "@/store/ai-ingest";
import { useQuestionFormatting } from "@/experiences/qna/composables/question-formatting";
import { SegmentStatus } from "@/types/segments";
import { QnAPanelViewTab, SourcesSortOption } from "@/experiences/qna/types";
import { QnAAnswerErrorReasonCode, AnalysisMode } from "@/types/ai";
// Helper functions
const getInitialQnAUIDataValue = (studies) => {
    return {
        studyIds: studies.map((study) => study.studyId) || [],
        sidebar: {
            state: false,
            pinned: false,
            width: undefined
        },
        tab: {
            index: QnAPanelViewTab.Question,
            scrollPositions: {},
            prev: undefined
        },
        followUp: {
            rootRequestId: undefined
        },
        historyFollowUp: {
            rootRequestId: undefined
        },
        verbatims: {
            requestId: undefined,
            rootRequestId: undefined,
            responseDate: undefined,
            answer: undefined,
            scrollPositions: {},
            grouped: true,
            sortOrder: SourcesSortOption.MostRelevant,
            pending: false,
            error: undefined
        },
        historyVerbatims: {
            requestId: undefined,
            rootRequestId: undefined,
            responseDate: undefined,
            answer: undefined,
            scrollPositions: {},
            grouped: true,
            sortOrder: SourcesSortOption.MostRelevant,
            pending: false,
            error: undefined
        },
        question: {
            text: "",
            analysisMode: AnalysisMode.STANDARD,
            filters: {
                studies,
                activities: [],
                tasks: [],
                categories: [],
                discussions: [],
                segments: [],
                participants: []
            },
            mentions: {},
            preserveFormData: false
        },
        history: {
            page: 0,
            pending: false,
            error: undefined
        },
        activeSourceHash: undefined,
        hasShownPredictiveModeIntro: false
    };
};
const getInitialUIStateValue = (studies) => {
    const rawStorageData = sessionStorage.getItem("QnAConfig");
    // Creates an object with no prototype
    const data = Object.create(null);
    // Use lodash merge function as suggested in Snyk's docs
    merge(data, getInitialQnAUIDataValue(studies));
    if (rawStorageData)
        merge(data, JSON.parse(rawStorageData));
    Object.keys(data).forEach((key) => {
        if (typeof data[key] !== "object" || Array.isArray(data[key]))
            return;
        if (data[key].pending !== false) {
            data[key].pending = false;
        }
        if (data[key].error !== undefined) {
            data[key].error = undefined;
        }
    });
    return data;
};
// Store definition
export const useQnAStore = defineStore("qna-store", () => {
    const i18n = inject("i18n");
    const { shouldThrowError } = useQnA();
    const { htmlToQuestion } = useQuestionFormatting();
    const usersStore = useStudyUsers();
    const { users } = storeToRefs(usersStore);
    const { currentStudyId, isModerator, isAdmin } = storeToRefs(useContext());
    const notifStore = useToastNotificationStore();
    const studiesStore = useStudiesStore();
    const { studies, studiesMap } = storeToRefs(studiesStore);
    const { segments, segmentsMap: allSegmentsMap } = storeToRefs(useSegmentsStore());
    const aiIngestStore = useAiIngestStore();
    const followUpConversations = ref(new Map());
    const fetchingFollowUpConversations = ref(new Set());
    const erroredFollowUpConversations = ref(new Map());
    const responseHistory = ref([]);
    const abortController = ref();
    const historyMode = computed(() => qnaUIState.value.tab.index >= 3);
    const followUpKey = computed(() => (historyMode.value ? "historyFollowUp" : "followUp"));
    const verbatimsKey = computed(() => (historyMode.value ? "historyVerbatims" : "verbatims"));
    const fetchingFilterOptions = ref(false);
    const studyActivitiesMap = ref(new Map());
    const availableStudyActivitiesMap = computed(() => {
        // FIXME: computed refs should be pure functions that derive values, not execute side effects.
        // Access embeddingBackfillState to establish reactive dependency; updates to this state will trigger recomputation
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const embeddingBackfillState = aiIngestStore.embeddingBackfillState;
        return new Map(Array.from(studyActivitiesMap.value.keys()).map((studyId) => {
            const activities = studyActivitiesMap.value.get(studyId) || new Map();
            return [
                studyId,
                new Map(Array.from(activities.entries()).filter(([_, a]) => !a.deleted && aiIngestStore.isActivityIngested(a)))
            ];
        }));
    });
    const availableStudies = computed(() => studies.value.filter((study) => study.hasParticipantContent &&
        study.permissions.aiEnabled &&
        (isModerator.value || (isAdmin.value && study.permissions.aiClientVisible)) &&
        (availableStudyActivitiesMap.value.get(study.studyId)?.size ||
            studyDiscussionsMap.value.get(study.studyId)?.topics.size)));
    const hasResponsesInStudies = computed(() => currentStudyId.value !== undefined
        ? studiesStore.getStoredStudyById(currentStudyId.value)?.hasParticipantContent &&
            availableStudies.value.some((s) => s.studyId === currentStudyId.value)
        : availableStudies.value.some((study) => study?.hasParticipantContent));
    // Update task map structure to support cross-study mode
    // Use a nested map structure: studyId -> activityId -> Task[]
    const allTasksMap = ref(new Map());
    // Discussion map structure: studyId -> {categories, topics}
    // Categories: categoryId -> DiscussionCategory
    // Topics: topicId -> DiscussionTopic
    const discussions = ref([]);
    const studyDiscussionsMap = computed(() => {
        const processCategory = (studyId, category, categoryMap, topicMap, parentCategoryId) => {
            const { categoryId, topics, subcategories, ...rest } = category;
            categoryMap.set(categoryId, { ...rest, studyId, categoryId, parentCategoryId });
            topics?.forEach((topic) => topicMap.set(topic.topicId, { ...topic, studyId, categoryId }));
            subcategories?.forEach((sub) => processCategory(studyId, sub, categoryMap, topicMap, categoryId));
        };
        const discussionMap = new Map();
        for (const { studyId, categories, topics } of discussions.value) {
            const categoryMap = new Map();
            const topicMap = new Map();
            categories?.forEach((cat) => processCategory(studyId, cat, categoryMap, topicMap));
            topics?.forEach((topic) => topicMap.set(topic.topicId, { ...topic, studyId }));
            discussionMap.set(studyId, { categories: categoryMap, topics: topicMap });
        }
        return discussionMap;
    });
    // Helper function to get a unique key for a study-activity pair
    const getStudyActivityKey = (studyId, activityId) => `${studyId}-${activityId}`;
    // Computed property to get non-deleted tasks
    const taskMap = computed(() => {
        const result = new Map();
        // Iterate through all studies
        allTasksMap.value.forEach((activityTaskMap, studyId) => {
            // Iterate through all activities in this study
            activityTaskMap.forEach((tasks, activityId) => {
                const key = getStudyActivityKey(studyId, activityId);
                // Filter out deleted tasks
                result.set(key, Array.from(tasks.values()).filter((t) => !t.deleted));
            });
        });
        return result;
    });
    const activeSegments = computed(() => segments.value.filter((s) => s.status === SegmentStatus.Active));
    const segmentFilterOptions = computed(() => {
        const studyIds = qnaUIState.value.question.filters.studies.length
            ? qnaUIState.value.question.filters.studies.map((s) => s.studyId)
            : availableStudies.value.map((study) => study.studyId);
        const selectedSegmentIds = new Set(qnaUIState.value.question.filters.segments.map((s) => s.segmentId));
        return [
            ...activeSegments.value.filter(({ segmentId, visibleToAllStudies, studyVisibilityIds }) => {
                return (visibleToAllStudies ||
                    selectedSegmentIds.has(segmentId) ||
                    (studyIds.length && studyVisibilityIds.some((id) => studyIds.includes(id))));
            })
        ];
    });
    // Helper function to get tasks for a specific activity
    const getTasksForActivity = (studyId, activityId) => {
        const key = getStudyActivityKey(studyId, activityId);
        return taskMap.value.get(key) || [];
    };
    const abortQuestion = () => {
        if (!(abortController.value instanceof AbortController))
            return;
        abortController.value.abort();
        abortController.value = undefined;
    };
    const fetchResponseHistory = async () => {
        qnaUIState.value.history.pending = true;
        qnaUIState.value.history.error = undefined;
        AiApi.qnaFetchResponseHistory(currentStudyId.value)
            .then((res) => {
            responseHistory.value = res;
        })
            .catch((err) => {
            console.error(err);
            responseHistory.value = [];
            qnaUIState.value.history.error = err;
        })
            .finally(() => {
            qnaUIState.value.history.pending = false;
        });
    };
    const fetchResponse = async (requestId) => {
        const key = historyMode.value ? "historyVerbatims" : "verbatims";
        const current = qnaUIState.value[key];
        qnaUIState.value[key] = {
            ...current,
            requestId,
            responseDate: undefined,
            pending: true,
            error: undefined
        };
        return AiApi.qnaFetchResponse(requestId, currentStudyId.value)
            .then((response) => {
            if (!response?.answer && !response?.error)
                throw new Error("Invalid response from server");
            qnaUIState.value[key].answer = response;
            qnaUIState.value[key].responseDate = new Date().toISOString();
            return response;
        })
            .catch((err) => {
            qnaUIState.value[key].error = err;
        })
            .finally(() => {
            qnaUIState.value[key].pending = false;
        });
    };
    const deleteResponse = async (requestId) => {
        qnaUIState.value.history.error = undefined;
        qnaUIState.value.history.pending = true;
        AiApi.qnaDeleteResponse(requestId)
            .then(() => {
            notifStore.addSuccessNotice(i18n.t("Question deleted"));
            responseHistory.value = responseHistory.value.filter((item) => item.requestId !== requestId);
        })
            .catch((err) => {
            console.error(err);
            qnaUIState.value.history.error = err;
        })
            .finally(() => {
            qnaUIState.value.history.pending = false;
        });
    };
    const askInitialQuestion = async (data, attempts = 5) => {
        abortQuestion();
        qnaUIState.value.question.preserveFormData = false;
        const responseDate = new Date();
        const rootRequestId = crypto.randomUUID();
        data.requestId = rootRequestId;
        qnaUIState.value.question.rootRequestId = rootRequestId;
        qnaUIState.value.followUp = { rootRequestId, scope: data.scope };
        const conversation = [
            {
                responseDate,
                ...data
            }
        ];
        followUpConversations.value.set(rootRequestId, conversation);
        abortController.value = new AbortController();
        qnaUIState.value.tab.index = QnAPanelViewTab.FollowUp;
        fetchingFollowUpConversations.value.add(rootRequestId);
        erroredFollowUpConversations.value.clear();
        const normalizedData = {
            ...data,
            question: htmlToQuestion(data.question)
        };
        return AiApi.qnaPostQuestion(normalizedData, abortController.value, currentStudyId.value)
            .then((response) => {
            if (!response?.answer && !response?.error)
                throw new Error(QnAAnswerErrorReasonCode.InternalServerError);
            const { error } = response;
            if (error && shouldThrowError(error)) {
                throw error;
            }
            followUpConversations.value.set(rootRequestId, [...conversation, response]);
            return response;
        })
            .catch((err) => {
            const { message, statusCode, reasonCode, error, segmentNames } = err;
            if (message === "canceled")
                return;
            if (statusCode === 500 && attempts > 1) {
                console.error(`${rootRequestId} can't be used as a conversation id. Retrying.`);
                fetchingFollowUpConversations.value.delete(rootRequestId);
                return askInitialQuestion(data, attempts - 1);
            }
            erroredFollowUpConversations.value.set(rootRequestId, {
                reasonCode: reasonCode ?? error ?? statusCode ?? message,
                segmentNames
            });
            qnaUIState.value.tab.index = QnAPanelViewTab.Question;
            qnaUIState.value.followUp.rootRequestId = undefined;
            return;
        })
            .finally(() => {
            fetchingFollowUpConversations.value.delete(rootRequestId);
        });
    };
    const fetchFollowUpConversation = async (rootRequestId, preservePendingState) => {
        fetchingFollowUpConversations.value.add(rootRequestId);
        erroredFollowUpConversations.value.delete(rootRequestId);
        const question = qnaUIState.value.question;
        if (question.rootRequestId !== rootRequestId) {
            followUpConversations.value.set(rootRequestId, []);
        }
        return AiApi.qnaFetchFollowUpConversation(rootRequestId, currentStudyId.value)
            .then((response) => {
            if (response.length) {
                followUpConversations.value.set(rootRequestId, response.flatMap(({ requestId, question, scope, analysisMode, responseDate, ...itemData }) => {
                    const processedItemData = { ...itemData };
                    return [
                        {
                            rootRequestId,
                            requestId,
                            responseDate,
                            question,
                            scope,
                            analysisMode: analysisMode || AnalysisMode.STANDARD
                        },
                        {
                            ...processedItemData,
                            requestId,
                            rootRequestId,
                            responseDate,
                            question,
                            scope,
                            analysisMode: analysisMode || AnalysisMode.STANDARD
                        }
                    ];
                }));
                return followUpConversations.value.get(rootRequestId);
            }
            return [];
        })
            .catch((err) => {
            if (err.message === "canceled")
                return;
            console.error(err);
            followUpConversations.value.set(rootRequestId, []);
            erroredFollowUpConversations.value.set(rootRequestId, err);
        })
            .finally(() => {
            if (!preservePendingState)
                fetchingFollowUpConversations.value.delete(rootRequestId);
        });
    };
    const askFollowUpQuestion = async ({ question, analysisMode }) => {
        const key = historyMode.value ? "historyFollowUp" : "followUp";
        const rootRequestId = qnaUIState.value[key].rootRequestId;
        if (!rootRequestId) {
            console.warn("qna.store.ts > askFollowUpQuestion(): Can't find rootRequestId.");
            return;
        }
        const conversation = followUpConversations.value.get(rootRequestId);
        if (!conversation?.[0]?.requestId) {
            console.warn("qna.store.ts > askFollowUpQuestion(): Can't find initialized conversation.");
            return;
        }
        const questionIndex = conversation.length;
        const { scope } = conversation[0];
        const responseDate = new Date();
        const requestId = crypto.randomUUID();
        conversation.push({ requestId, rootRequestId, responseDate, scope, question, analysisMode });
        abortQuestion();
        abortController.value = new AbortController();
        fetchingFollowUpConversations.value.add(rootRequestId);
        const isHistorical = qnaUIState.value.tab.index === QnAPanelViewTab.HistoryFollowUp;
        const normalizedQuestion = htmlToQuestion(question);
        return AiApi.qnaPostQuestion({
            question: normalizedQuestion,
            scope,
            analysisMode,
            requestId,
            rootRequestId,
            isHistorical
        }, abortController.value, currentStudyId.value)
            .then((res) => {
            const conversation = followUpConversations.value.get(rootRequestId);
            if (!conversation?.[questionIndex]?.requestId || !res.requestId) {
                const error = res.error && shouldThrowError(res.error)
                    ? res.error
                    : { reasonCode: QnAAnswerErrorReasonCode.InternalServerError };
                throw error;
            }
            conversation.push({ ...res, responseDate });
            return res;
        })
            .catch((err) => {
            if (err.message === "canceled")
                return;
            conversation.push({
                ...conversation[questionIndex],
                error: { reasonCode: err.reasonCode ?? err.message ?? err, segmentNames: err.segmentNames }
            });
        })
            .finally(() => {
            fetchingFollowUpConversations.value.delete(rootRequestId);
        });
    };
    const fetchAllActivitiesWithResponses = async () => {
        // Fetch all activities with responses across all studies in a single call
        if (fetchingFilterOptions.value)
            return;
        fetchingFilterOptions.value = true;
        return ActivitiesApi.getSiteActivities(true)
            .then((items) => {
            items.forEach((activity) => {
                if (!studyActivitiesMap.value.has(activity.studyId)) {
                    studyActivitiesMap.value.set(activity.studyId, new Map());
                }
                studyActivitiesMap.value.get(activity.studyId).set(activity.activityId, activity);
            });
        })
            .finally(() => (fetchingFilterOptions.value = false));
    };
    const fetchQnATaskFilterOptions = async (studyId) => {
        // If tasks for this study are already fetched, no need to fetch again
        if (allTasksMap.value.has(studyId)) {
            return;
        }
        return TasksApi.getStudyTasks(studyId, true)
            .then((items) => {
            // Initialize the nested map structure for this study
            const activityMap = new Map();
            // Group tasks by activity
            items.forEach((task) => {
                if (!activityMap.has(task.activityId)) {
                    activityMap.set(task.activityId, new Map());
                }
                activityMap.get(task.activityId).set(task.taskId, task);
            });
            // Store the activity map for this study
            allTasksMap.value.set(studyId, activityMap);
        })
            .catch((error) => {
            console.error("Failed to fetch task filter options", error);
        });
    };
    const fetchQnADiscussionFilterOptions = async () => {
        try {
            discussions.value = await DiscussionsApi.getQnADiscussions();
            if (!Array.isArray(discussions.value)) {
                const summary = typeof discussions.value === "object" && discussions.value !== null && "length" in discussions.value
                    ? `length=${discussions.value.length}`
                    : "";
                throw new Error(`Expected array of discussions, received: ${typeof discussions.value} ${summary}`);
            }
        }
        catch (error) {
            console.error(`Failed to fetch discussion filter options`, error);
            if (!Array.isArray(discussions.value)) {
                discussions.value = [];
            }
        }
    };
    const studyHasActivities = (studyId) => !!studyActivitiesMap.value.get(studyId);
    const studyHasDiscussions = (studyId) => !!studyDiscussionsMap.value.get(studyId);
    const sendStudyDataForAiProcessing = async (requestDto) => {
        return AiApi.postStudyDataForAiProcessing(requestDto);
    };
    // UI State persistence
    const qnaUIState = ref(getInitialUIStateValue(currentStudyId.value
        ? [...(studiesMap.value.get(currentStudyId.value) ? [studiesMap.value.get(currentStudyId.value)] : [])]
        : []));
    watch(qnaUIState, (next) => {
        sessionStorage.setItem("QnAConfig", JSON.stringify(next));
    }, { deep: true, immediate: true });
    // Fetch users data when some conversation filters refer to users that are not yet loaded
    watch(() => qnaUIState.value[followUpKey.value].rootRequestId
        ? followUpConversations.value.get(qnaUIState.value[followUpKey.value].rootRequestId)?.[0]?.scope
            ?.userIds
        : undefined, (next) => {
        const missingParticipants = next?.filter((userId) => !users.value[userId]);
        if (missingParticipants?.length) {
            usersStore.getUsers(missingParticipants);
        }
    });
    // Fetch users data when some question filters refer to users that are not yet loaded
    watch(() => qnaUIState.value.question.filters.participants, (next) => {
        if (!next.length)
            return;
        const missingParticipants = next.reduce((acc, { userId }) => {
            const id = Number(userId);
            if (!users.value[id])
                acc.push(id);
            return acc;
        }, []);
        if (missingParticipants.length) {
            usersStore.getUsers(missingParticipants).then((items) => {
                const userIds = items.map(({ userId }) => userId);
                qnaUIState.value.question.filters.participants = [
                    ...next.filter(({ userId }) => !userIds.includes(Number(userId))),
                    ...items.map(({ displayName: name, userId }) => ({ name, userId, unavailable: false }))
                ];
            });
        }
    });
    const resetQuestionContext = () => {
        resetQnAUIState({
            sidebar: { ...qnaUIState.value.sidebar }
        });
    };
    const resetQnAUIState = (overrides) => {
        const currStudy = currentStudyId.value && studiesMap.value.get(currentStudyId.value); // Will resolve to `false` when there is no current study
        const { analysisMode } = qnaUIState.value.question;
        const initialState = getInitialQnAUIDataValue(currStudy ? [currStudy] : []);
        qnaUIState.value = {
            ...initialState,
            // analysisMode must never be reset
            question: {
                ...initialState.question,
                analysisMode
            },
            ...(overrides ?? {})
        };
    };
    const openWithContext = async (ctx) => {
        resetQuestionContext();
        try {
            // Study filter
            const study = availableStudies.value.find((s) => s.studyId === ctx.studyId);
            if (study) {
                qnaUIState.value.question.filters.studies = [study];
            }
            // Activity filter
            if (ctx.activityId && ctx.activityId > 0 && !ctx.taskId) {
                await fetchAllActivitiesWithResponses();
                const activity = availableStudyActivitiesMap.value.get(ctx.studyId)?.get(ctx.activityId);
                if (activity) {
                    qnaUIState.value.question.filters.activities = [activity];
                }
            }
            // Task filter
            if (ctx.activityId !== undefined && ctx.taskId && ctx.taskId > 0) {
                await fetchQnATaskFilterOptions(ctx.studyId);
                const task = allTasksMap.value.get(ctx.studyId)?.get(ctx.activityId)?.get(ctx.taskId);
                if (task) {
                    qnaUIState.value.question.filters.tasks = [task];
                }
            }
            // Discussion topic filter
            if (ctx.topicId) {
                await fetchQnADiscussionFilterOptions();
                const studyDiscussions = studyDiscussionsMap.value.get(ctx.studyId);
                if (studyDiscussions) {
                    const topic = studyDiscussions.topics.get(ctx.topicId);
                    if (topic) {
                        qnaUIState.value.question.filters.discussions = [{ ...topic, studyId: ctx.studyId }];
                        if (topic.categoryId) {
                            const category = studyDiscussions.categories.get(topic.categoryId);
                            if (category) {
                                qnaUIState.value.question.filters.categories = [{ ...category, studyId: ctx.studyId }];
                            }
                        }
                    }
                }
            }
            // Category filter
            if (ctx.categoryId && !ctx.topicId) {
                await fetchQnADiscussionFilterOptions();
                const category = studyDiscussionsMap.value.get(ctx.studyId)?.categories.get(ctx.categoryId);
                if (category) {
                    qnaUIState.value.question.filters.categories = [{ ...category, studyId: ctx.studyId }];
                }
            }
            // Participant filter
            if (ctx.participantId) {
                const user = users.value[ctx.participantId];
                if (user) {
                    qnaUIState.value.question.filters.participants = [{ ...user, name: user.displayName, unavailable: false }];
                }
            }
        }
        catch (error) {
            console.error("Failed to open QnA with context", error);
        }
        qnaUIState.value.sidebar.state = true;
    };
    // Cross-tab communication
    // Preserve sidebar state per-tab (sidebar is UI state, not shared data)
    const { initBroadcast: initUIStateBroadcast } = useCrossTabCom("QNA-UI-STATE-CH", {
        qnaUIState,
        responseHistory
    }, {
        preserveKeys: { qnaUIState: ["sidebar"] }
    });
    return {
        historyMode,
        followUpKey,
        verbatimsKey,
        fetchingFilterOptions,
        fetchQnATaskFilterOptions,
        fetchQnADiscussionFilterOptions,
        fetchAllActivitiesWithResponses,
        availableStudyActivitiesMap,
        studyActivitiesMap,
        taskMap,
        allTasksMap,
        studyDiscussionsMap,
        getTasksForActivity,
        getStudyActivityKey,
        activeSegments,
        segmentFilterOptions,
        allSegmentsMap,
        askInitialQuestion,
        abortQuestion,
        responseHistory,
        fetchResponseHistory,
        fetchResponse,
        deleteResponse,
        followUpConversations,
        fetchingFollowUpConversations,
        erroredFollowUpConversations,
        askFollowUpQuestion,
        fetchFollowUpConversation,
        qnaUIState,
        resetQuestionContext,
        resetQnAUIState,
        sendStudyDataForAiProcessing,
        initUIStateBroadcast,
        availableStudies,
        hasResponsesInStudies,
        studyHasActivities,
        studyHasDiscussions,
        openWithContext
    };
});
